import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon 
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { hasAdminAccess } from '../utils/roleUtils';
import { getStylingCompanies, createStylingCompany, updateStylingCompany, deleteStylingCompany, type StylingCompany } from '../services/stylingService';

const StylingCompanies = () => {
  const { agency, user } = useAuth();
  const [stylingCompanies, setStylingCompanies] = useState<StylingCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<StylingCompany>({
    name: '',
    email: '',
    phone: '',
    contactName: '',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    company: StylingCompany | null;
    index: number | null;
  }>({
    open: false,
    company: null,
    index: null,
  });

  // Check if user is admin for management actions
  const isAdmin = hasAdminAccess(user);

  useEffect(() => {
    if (agency?.slug) {
      fetchStylingCompanies();
    }
  }, [agency?.slug]);

  const fetchStylingCompanies = async () => {
    if (!agency?.slug) return;
    
    try {
      setLoading(true);
      const companies = await getStylingCompanies(agency.slug);
      setStylingCompanies(companies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (index?: number) => {
    if (index !== undefined) {
      setEditingIndex(index);
      const company = stylingCompanies[index];
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        contactName: company.contactName || '',
      });
    } else {
      setEditingIndex(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        contactName: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingIndex(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      contactName: '',
    });
  };

  const handleSave = async () => {
    if (!agency?.slug || !formData.name.trim()) return;

    try {
      setSaving(true);
      
      const companyData: StylingCompany = {
        name: formData.name.trim(),
        email: formData.email?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        contactName: formData.contactName?.trim() || undefined,
      };
      
      if (editingIndex !== null) {
        // Editing existing company
        const stylerId = stylingCompanies[editingIndex]._id;
        if (stylerId) {
          await updateStylingCompany(agency.slug, stylerId, companyData);
        }
      } else {
        // Adding new company
        await createStylingCompany(agency.slug, companyData);
      }

      await fetchStylingCompanies();
      
      const successMsg = editingIndex !== null ? 'Styling company updated successfully' : 'Styling company added successfully';
      setSuccess(successMsg);
      handleCloseDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save styling company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (index: number) => {
    const company = stylingCompanies[index];
    setConfirmDialog({
      open: true,
      company,
      index,
    });
  };

  const handleConfirmDelete = async () => {
    if (!agency?.slug || confirmDialog.index === null) return;

    try {
      setSaving(true);
      const stylerId = stylingCompanies[confirmDialog.index]._id;
      if (stylerId) {
        await deleteStylingCompany(agency.slug, stylerId);
        await fetchStylingCompanies();
        setSuccess('Styling company deleted successfully');
      }
      setConfirmDialog({ open: false, company: null, index: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete styling company');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>Loading styling companies...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Styling Companies
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Company
            </Button>
          )}
        </Box>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isAdmin ? 'Manage Styling Companies' : 'Styling Companies'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isAdmin 
              ? 'These companies will appear as options in the styling arrangement task for your agency.'
              : 'These are the styling companies available for your agency\'s styling arrangement tasks.'
            }
          </Typography>

          {stylingCompanies.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    {isAdmin && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stylingCompanies.map((company, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {company.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {company.contactName || '-'}
                      </TableCell>
                      <TableCell>
                        {company.email || '-'}
                      </TableCell>
                      <TableCell>
                        {company.phone || '-'}
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="right">
                          <IconButton onClick={() => handleOpenDialog(index)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleDelete(index)}
                            disabled={saving}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No styling companies configured yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add styling companies to provide options for the styling arrangement task.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Edit Styling Company' : 'Add New Styling Company'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              autoFocus
              label="Company Name *"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              helperText="Required"
            />
            <TextField
              label="Contact Person Name"
              fullWidth
              variant="outlined"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              helperText="Primary contact at this company"
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              helperText="Contact email address"
            />
            <TextField
              label="Phone"
              fullWidth
              variant="outlined"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              helperText="Contact phone number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.name.trim() || saving}
          >
            {saving ? 'Saving...' : (editingIndex !== null ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, company: null, index: null })}
      >
        <DialogTitle>Delete Styling Company</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{confirmDialog.company?.name}</strong>?
          </Typography>
          <Typography color="warning.main" sx={{ mt: 2 }}>
            This will remove the styling company from your agency.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, company: null, index: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StylingCompanies; 